import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { projectsAPI } from '@/api/projects';
import { employeesAPI } from '@/api/employees';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, BarChart3, Save, X, AlertTriangle, ExternalLink, MessageSquare, Loader2, RefreshCw, History, Trash2 } from 'lucide-react';
import { formatCurrency, formatDateShort, getHealthEmoji, getHealthText, formatPercent } from '@/utils/formatters';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  PROJECT_STATUS_OPTIONS,
  BLOCK_COLORS,
  BlockHeader,
  FieldLabel,
  MoneyInput,
  PercentInput,
  DatePickerField,
  VendorSelect,
  ManagerSelect,
  ClientSelect,
} from '@/components/project/FormHelpers';
import { bitrixAPI } from '@/api/bitrix';
import AlternativeSolutions from '@/components/project/AlternativeSolutions';

// Constants
const TS_STATUS_OPTIONS = [
  { value: 'готовится', label: 'Готовится' },
  { value: 'отдали', label: 'Отдали' },
  { value: 'в проработке', label: 'В проработке' },
];

const RISK_CATEGORY_OPTIONS = [
  { value: 'none', label: 'Не указано' },
  { value: 'Логистика', label: 'Логистика' },
  { value: 'Финансы', label: 'Финансы' },
  { value: 'Технический', label: 'Технический' },
  { value: 'Ресурсы', label: 'Ресурсы' },
];


const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [bitrixComments, setBitrixComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  // Stage 11.4: Lifecycle actions
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Editable form state
  const [formData, setFormData] = useState({});

  // Check user permissions
  const userRoleLower = user?.role?.toLowerCase();
  const canEdit = userRoleLower === 'admin' || userRoleLower === 'pm' || userRoleLower === 'moderator' || userRoleLower === 'finance_admin' || userRoleLower === 'senior_manager';
  const canViewGantt = canEdit;

  // Блоки "СУБПОДРЯД И НАЛОГИ" и "ИТОГОВАЯ КАЛЬКУЛЯЦИЯ" скрыты для Senior Manager
  const canViewFinanceBlocks = userRoleLower === 'admin' || userRoleLower === 'finance_admin';

  // Stage 11.6: Lifecycle permissions
  const isFinanceAdmin = userRoleLower === 'finance_admin';
  const isAdmin = userRoleLower === 'admin';
  const isBitrixLinked = project?.bitrix_task_id;
  const isPaid = project?.inputs?.payment_status === 'Paid';
  const bitrixCompleted = project?.bitrix_status === 'completed';
  const pccStatus = project?.pcc_status || 'Active';

  // Stage 11.6: DELETE — finance_admin может удалить любой НЕ-удалённый проект
  const canDelete = project && !project.deleted_at && isFinanceAdmin;

  // ARCHIVE: Bitrix-linked + Active + Paid + bitrix_status=completed
  const canArchive = project && isBitrixLinked && pccStatus === 'Active' &&
    isPaid && bitrixCompleted && isFinanceAdmin;

  // Stage 11.6: CLOSE — finance_admin может закрыть любой Active проект
  const canClose = project && pccStatus === 'Active' && isFinanceAdmin;

  // UNARCHIVE: Archived
  const canUnarchive = project && pccStatus === 'Archived' && isFinanceAdmin;

  // REOPEN: Bitrix-linked + Closed
  const canReopen = project && isBitrixLinked && pccStatus === 'Closed' && isFinanceAdmin;

  // RESTORE: deleted_at IS NOT NULL (soft-deleted)
  const canRestore = project?.deleted_at && isAdmin;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectData, employeesData] = await Promise.all([
        projectsAPI.getById(id),
        employeesAPI.getAll().catch(() => []), // Handle auth error gracefully
      ]);
      setProject(projectData);
      setEmployees(employeesData);
      initFormData(projectData);
      // Load Bitrix comments and sync stages if task is linked
      if (projectData.bitrix_task_id) {
        loadBitrixComments(projectData.bitrix_task_id);
        syncBitrixStages(projectData.bitrix_task_id, projectData.id, projectData);
      }
    } catch (error) {
      showError(error, 'Ошибка загрузки проекта');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadBitrixComments = async (taskId) => {
    try {
      setCommentsLoading(true);
      const data = await bitrixAPI.getTaskComments(taskId);
      setBitrixComments(data.comments || []);
    } catch (error) {
      console.warn('Failed to load Bitrix comments:', error);
      setBitrixComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const syncBitrixStages = async (taskId, projectId, projectData) => {
    try {
      const result = await bitrixAPI.syncStages(taskId, projectId);
      if (result.synced && result.stages) {
        setProject(prev => prev ? { ...prev, stages: result.stages } : prev);
      }
    } catch (error) {
      console.warn('Failed to sync Bitrix stages:', error);
    }
  };

  const initFormData = (data) => {
    setFormData({
      // Basic info
      name: data.name || '',
      description: data.description || '',
      client: { name: data.client_name || '', group_id: data.client_bitrix_group_id || null },
      expected_end_date: data.expected_end_date ? new Date(data.expected_end_date) : null,
      
      // Team
      responsible_id: data.responsible_id || '',
      co_executor_ids: data.co_executor_ids || [],
      
      // Presale
      ts_status: data.ts_status || '',
      ts_deadline: data.ts_deadline ? new Date(data.ts_deadline) : null,
      vendor_name: data.vendor_name || '',
      supplier_name: data.supplier_name || '',
      risk_category: data.risk_category || 'none',
      
      // Block B: Logistics
      delivery_eta: data.delivery_eta ? new Date(data.delivery_eta) : null,
      installation_start: data.installation_start ? new Date(data.installation_start) : null,
      
      // Block C: Risk Matrix
      risk_probability: data.risk_probability || '',
      risk_impact: data.risk_impact || '',
      
      // Status comment (for reports)
      status_comment: data.status_comment || '',

      // === STAGE 10.2: 32-FIELD FORM ===
      // БЛОК 1: ПАСПОРТ (new fields — name/client/vendor already above)
      year: data.year ?? null,
      manager: {
        name: data.manager || data.responsible_bitrix_name || '',
        bitrix_id: data.manager_bitrix_id || data.responsible_bitrix_id || null,
      },
      project_status: data.project_status || '',
      client_budget_with_vat: data.client_budget_with_vat ?? null,

      // БЛОК 2: ОЦЕНКА
      kp_amount_with_vat: data.kp_amount_with_vat ?? null,
      gpl_project_with_vat: data.gpl_project_with_vat ?? null,
      entry_cost_with_vat: data.entry_cost_with_vat ?? null,

      // БЛОК 3: ОБЯЗАТЕЛЬСТВА
      contract_amount_with_vat: data.contract_amount_with_vat ?? null,
      contract_sign_date: data.contract_sign_date ? new Date(data.contract_sign_date) : null,
      contract_close_date: data.contract_close_date ? new Date(data.contract_close_date) : null,
      vendor_payment_plan_date: data.vendor_payment_plan_date ? new Date(data.vendor_payment_plan_date) : null,
      vendor_payment_fact_amount: data.vendor_payment_fact_amount ?? null,
      client_payment_fact_date: data.client_payment_fact_date ? new Date(data.client_payment_fact_date) : null,

      // БЛОК 4: ЗАТРАТЫ
      expenses_equipment: data.expenses_equipment ?? null,
      expenses_logistics: data.expenses_logistics ?? null,
      expenses_other: data.expenses_other ?? null,
      expenses_installation: data.expenses_installation ?? null,
      expenses_overtime: data.expenses_overtime ?? null,
      expenses_insurance: data.expenses_insurance ?? null,
      expenses_credit: data.expenses_credit ?? null,
      expenses_partnership: data.expenses_partnership ?? null,
      expenses_penalty: data.expenses_penalty ?? null,

      // БЛОК 5: СУБПОДРЯД И НАЛОГИ
      subcontract_percent: data.subcontract_percent ?? null,
      subcontract_amount: data.subcontract_amount ?? null,
      taxes_amount: data.taxes_amount ?? null,

      // БЛОК 6: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ
      result_margin: data.result_margin ?? null,
      result_margin_percent: data.result_margin_percent ?? null,
      result_taxes: data.result_taxes ?? null,
      result_gross_profit: data.result_gross_profit ?? null,

      // БЛОК 7: ПЛАН/ФАКТ (только для Finance Admin)
      budget_plan: data.finances?.budget_plan ?? null,
      budget_fact: data.finances?.budget_fact ?? null,
      costs_fact: data.finances?.costs_fact ?? null,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Автосохранение статуса проекта (работает вне режима редактирования)
  const handleStatusChange = async (newStatus) => {
    setFormData(prev => ({ ...prev, project_status: newStatus }));
    // Если не в режиме редактирования — сохраняем сразу
    if (!editMode) {
      try {
        await projectsAPI.update(id, { project_status: newStatus });
        // Перезагружаем проект чтобы получить обновлённую историю статусов
        const updatedProject = await projectsAPI.getById(id);
        setProject(updatedProject);
        showSuccess('Статус обновлён');
      } catch (error) {
        showError(error, 'Ошибка обновления статуса');
      }
    }
  };

  // Автосохранение итогового комментария (работает для всех ролей без режима редактирования)
  const handleStatusCommentSave = async (newComment) => {
    // Если не в режиме редактирования — сохраняем сразу при потере фокуса
    if (!editMode) {
      try {
        await projectsAPI.update(id, { status_comment: newComment || null });
        showSuccess('Комментарий сохранён');
      } catch (error) {
        showError(error, 'Ошибка сохранения комментария');
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Ensure manager employee exists in PCC (for responsible_id sync)
      let responsibleId = formData.responsible_id || null;
      const managerName = formData.manager?.name?.trim() || '';
      const managerBitrixId = formData.manager?.bitrix_id || null;

      if (managerName && managerBitrixId) {
        try {
          const empResult = await bitrixAPI.ensureEmployee(managerName, managerBitrixId);
          responsibleId = empResult.employee_id;
        } catch {
          // Keep existing responsible_id on error
        }
      }

      // Prepare update data
      const updateData = {
        name: formData.name,
        description: formData.description || null,
        client_name: formData.client?.name || '',
        client_bitrix_group_id: formData.client?.group_id || null,
        expected_end_date: formData.expected_end_date ? format(formData.expected_end_date, 'yyyy-MM-dd') : null,
        responsible_id: responsibleId,
        co_executor_ids: formData.co_executor_ids || [],
        ts_status: formData.ts_status || null,
        ts_deadline: formData.ts_deadline ? format(formData.ts_deadline, 'yyyy-MM-dd') : null,
        vendor_name: formData.vendor_name || null,
        supplier_name: formData.supplier_name || null,
        risk_category: formData.risk_category === 'none' ? null : (formData.risk_category || null),
        delivery_eta: formData.delivery_eta ? format(formData.delivery_eta, 'yyyy-MM-dd') : null,
        installation_start: formData.installation_start ? format(formData.installation_start, 'yyyy-MM-dd') : null,
        risk_probability: formData.risk_probability ? parseInt(formData.risk_probability) : null,
        risk_impact: formData.risk_impact ? parseInt(formData.risk_impact) : null,
        status_comment: formData.status_comment || null,

        // === STAGE 10.2: 32-FIELD FORM ===
        // БЛОК 1: ПАСПОРТ
        year: formData.year ? parseInt(formData.year) : null,
        manager: managerName || null,
        manager_bitrix_id: managerBitrixId,
        project_status: formData.project_status || null,
        client_budget_with_vat: formData.client_budget_with_vat,

        // БЛОК 2: ОЦЕНКА
        kp_amount_with_vat: formData.kp_amount_with_vat,
        gpl_project_with_vat: formData.gpl_project_with_vat,
        entry_cost_with_vat: formData.entry_cost_with_vat,

        // БЛОК 3: ОБЯЗАТЕЛЬСТВА
        contract_amount_with_vat: formData.contract_amount_with_vat,
        contract_sign_date: formData.contract_sign_date ? format(formData.contract_sign_date, 'yyyy-MM-dd') : null,
        contract_close_date: formData.contract_close_date ? format(formData.contract_close_date, 'yyyy-MM-dd') : null,
        vendor_payment_plan_date: formData.vendor_payment_plan_date ? format(formData.vendor_payment_plan_date, 'yyyy-MM-dd') : null,
        vendor_payment_fact_amount: formData.vendor_payment_fact_amount,
        client_payment_fact_date: formData.client_payment_fact_date ? format(formData.client_payment_fact_date, 'yyyy-MM-dd') : null,

        // БЛОК 4: ЗАТРАТЫ
        expenses_equipment: formData.expenses_equipment,
        expenses_logistics: formData.expenses_logistics,
        expenses_other: formData.expenses_other,
        expenses_installation: formData.expenses_installation,
        expenses_overtime: formData.expenses_overtime,
        expenses_insurance: formData.expenses_insurance,
        expenses_credit: formData.expenses_credit,
        expenses_partnership: formData.expenses_partnership,
        expenses_penalty: formData.expenses_penalty,

        // БЛОК 5: СУБПОДРЯД И НАЛОГИ
        subcontract_percent: formData.subcontract_percent,
        subcontract_amount: formData.subcontract_amount,
        taxes_amount: formData.taxes_amount,

        // БЛОК 6: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ
        result_margin: formData.result_margin,
        result_margin_percent: formData.result_margin_percent,
        result_taxes: formData.result_taxes,
        result_gross_profit: formData.result_gross_profit,

        // БЛОК 7: ПЛАН/ФАКТ (finances)
        finances: {
          budget_plan: formData.budget_plan || 0,
          budget_fact: formData.budget_fact || 0,
          costs_fact: formData.costs_fact || 0,
        },
      };

      await projectsAPI.update(id, updateData);
      showSuccess('Проект сохранён');
      setEditMode(false);
      await loadData(); // Reload to get calculated fields
    } catch (error) {
      showError(error, 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    initFormData(project);
    setEditMode(false);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await projectsAPI.delete(id);
      showSuccess('Проект удалён');
      navigate('/projects');
    } catch (error) {
      showError(error, 'Ошибка удаления проекта');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleClose = async () => {
    try {
      setClosing(true);
      await projectsAPI.close(id);
      showSuccess('Проект закрыт');
      // Reload project to show updated status
      const updated = await projectsAPI.getById(id);
      setProject(updated);
    } catch (error) {
      showError(error, 'Ошибка закрытия проекта');
    } finally {
      setClosing(false);
      setShowCloseConfirm(false);
    }
  };

  // Stage 11.4: Lifecycle handlers
  const handleArchive = async () => {
    try {
      setArchiving(true);
      await projectsAPI.archive(id);
      showSuccess('Проект архивирован');
      const updated = await projectsAPI.getById(id);
      setProject(updated);
    } catch (error) {
      showError(error, 'Ошибка архивирования проекта');
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleUnarchive = async () => {
    try {
      setUnarchiving(true);
      await projectsAPI.unarchive(id);
      showSuccess('Проект разархивирован');
      const updated = await projectsAPI.getById(id);
      setProject(updated);
    } catch (error) {
      showError(error, 'Ошибка разархивирования проекта');
    } finally {
      setUnarchiving(false);
      setShowUnarchiveConfirm(false);
    }
  };

  const handleReopen = async () => {
    try {
      setReopening(true);
      await projectsAPI.reopen(id);
      showSuccess('Проект переоткрыт');
      const updated = await projectsAPI.getById(id);
      setProject(updated);
    } catch (error) {
      showError(error, 'Ошибка переоткрытия проекта');
    } finally {
      setReopening(false);
      setShowReopenConfirm(false);
    }
  };

  const handleRestore = async () => {
    try {
      setRestoring(true);
      await projectsAPI.restore(id);
      showSuccess('Проект восстановлен');
      const updated = await projectsAPI.getById(id);
      setProject(updated);
    } catch (error) {
      showError(error, 'Ошибка восстановления проекта');
    } finally {
      setRestoring(false);
      setShowRestoreConfirm(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.full_name : empId || '—';
  };


  // Check delivery risk
  const hasDeliveryRisk = () => {
    if (!formData.delivery_eta || !formData.installation_start) return false;
    return new Date(formData.delivery_eta) > new Date(formData.installation_start);
  };

  // Check critical risk
  const hasCriticalRisk = () => {
    const prob = parseInt(formData.risk_probability) || 0;
    const impact = parseInt(formData.risk_impact) || 0;
    return prob * impact > 15;
  };

  // Redirect to previous list (or /projects fallback) with toast if project not found
  useEffect(() => {
    if (!loading && !project) {
      showError(new Error('Проект не найден или был удалён'));
      // Return to the list user came from (preserves filters/page), or fallback to /projects
      const returnUrl = location.state?.returnUrl || '/projects';
      navigate(returnUrl, { replace: true });
    }
  }, [loading, project, navigate, location.state]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Загрузка...</div>;
  }

  if (!project) {
    // Will redirect via useEffect above, show loading state briefly
    return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  const getHealthBadgeColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isReadOnly = !canEdit || !editMode;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="project-detail-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Назад
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-sm text-gray-600 mt-1">Клиент: {project.client_name}</p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Stage 11.4: Lifecycle buttons */}
              {canArchive && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowArchiveConfirm(true)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  data-testid="archive-project-btn"
                >
                  Архивировать
                </Button>
              )}
              {canClose && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCloseConfirm(true)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  data-testid="close-project-btn"
                >
                  Закрыть проект
                </Button>
              )}
              {canUnarchive && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUnarchiveConfirm(true)}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  data-testid="unarchive-project-btn"
                >
                  Разархивировать
                </Button>
              )}
              {canReopen && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReopenConfirm(true)}
                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  data-testid="reopen-project-btn"
                >
                  Переоткрыть
                </Button>
              )}
              {canRestore && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRestoreConfirm(true)}
                  className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                  data-testid="restore-project-btn"
                >
                  Восстановить
                </Button>
              )}
              {canDelete && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Удалить
                </Button>
              )}
              {canEdit && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  data-testid="edit-project-btn"
                >
                  Редактировать
                </Button>
              )}
              {editMode && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-project-btn"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </>
              )}
              {canViewGantt && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${id}/gantt`)}
                  className="flex items-center gap-2"
                  data-testid="view-gantt-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                  План-график
                </Button>
              )}
              <Badge className={getHealthBadgeColor(project.health_status)}>
                {getHealthEmoji(project.health_status)} {getHealthText(project.health_status)}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">

            {/* ═══════════ БЛОК 1: ПАСПОРТ ═══════════ */}
            <Card>
              <CardContent className="pt-5 space-y-4">
                <BlockHeader title="ПАСПОРТ" color={BLOCK_COLORS.passport} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <FieldLabel>ID Задачи (Bitrix)</FieldLabel>
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded-md text-sm min-h-[36px]">
                      {project.bitrix_task_id ? (
                        <span>{project.bitrix_task_id}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Год</FieldLabel>
                    <Input
                      type="number"
                      value={formData.year ?? ''}
                      onChange={(e) => handleInputChange('year', e.target.value)}
                      disabled={isReadOnly}
                      className="text-center"
                      min={2020}
                      max={2035}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Заказчик</FieldLabel>
                    <ClientSelect
                      value={formData.client}
                      onChange={(v) => handleInputChange('client', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <FieldLabel>Наименование проекта</FieldLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Менеджер</FieldLabel>
                    <ManagerSelect
                      value={formData.manager}
                      onChange={(v) => handleInputChange('manager', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Вендор</FieldLabel>
                    <VendorSelect
                      value={formData.vendor_name}
                      onChange={(v) => handleInputChange('vendor_name', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Поставщик</FieldLabel>
                    <Input
                      value={formData.supplier_name || ''}
                      onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Название поставщика"
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Статус проекта</FieldLabel>
                    {(() => {
                      const standardValues = PROJECT_STATUS_OPTIONS.map(o => o.value);
                      const isCustom = formData.project_status && !standardValues.includes(formData.project_status);
                      const showCustomInput = isCustom || formData._customStatusMode;

                      if (showCustomInput) {
                        return (
                          <div className="flex gap-2">
                            <Input
                              value={formData.project_status || ''}
                              onChange={(e) => handleInputChange('project_status', e.target.value)}
                              onBlur={(e) => handleStatusChange(e.target.value)}
                              disabled={!canEdit}
                              placeholder="Введите статус вручную"
                              className="flex-1"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, _customStatusMode: false, project_status: '' }));
                              }}
                              disabled={!canEdit}
                            >
                              Список
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <Select
                          value={formData.project_status}
                          onValueChange={(v) => {
                            if (v === '__custom__') {
                              setFormData(prev => ({ ...prev, _customStatusMode: true, project_status: '' }));
                            } else {
                              handleStatusChange(v);
                            }
                          }}
                          disabled={!canEdit}
                        >
                          <SelectTrigger
                            className={cn(
                              !formData.project_status && "border-red-500 bg-red-50 ring-red-200"
                            )}
                          >
                            <SelectValue placeholder="Выберите статус" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROJECT_STATUS_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                            <SelectItem value="__custom__">Другой (ввести вручную)</SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    })()}
                    {!formData.project_status && (
                      <p className="text-xs text-red-500 mt-1">Статус не выбран</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Бюджет от заказчика с НДС</FieldLabel>
                    <MoneyInput
                      value={formData.client_budget_with_vat}
                      onChange={(v) => handleInputChange('client_budget_with_vat', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Соисполнители</FieldLabel>
                    <div className={cn(
                      "flex items-center px-3 py-2 border rounded-md text-sm min-h-[36px]",
                      isReadOnly ? "bg-gray-50" : "bg-background"
                    )}>
                      {formData.co_executor_ids?.length > 0
                        ? formData.co_executor_ids.map(id => getEmployeeName(id)).join(', ')
                        : <span className="text-gray-400">—</span>
                      }
                    </div>
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Статус ТС</FieldLabel>
                    <Select
                      value={formData.ts_status}
                      onValueChange={(v) => handleInputChange('ts_status', v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите" />
                      </SelectTrigger>
                      <SelectContent>
                        {TS_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Срок ТС</FieldLabel>
                    <DatePickerField
                      value={formData.ts_deadline}
                      onChange={(v) => handleInputChange('ts_deadline', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <FieldLabel>Описание</FieldLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={isReadOnly}
                      rows={3}
                      placeholder="Описание проекта..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Альтернативные решения */}
            <AlternativeSolutions projectId={id} />

            {/* ═══════════ БЛОК 2: ОЦЕНКА ═══════════ */}
            <Card>
              <CardContent className="pt-5 space-y-4">
                <BlockHeader title="ОЦЕНКА" color={BLOCK_COLORS.evaluation} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <FieldLabel>Сумма КП с НДС</FieldLabel>
                    <MoneyInput
                      value={formData.kp_amount_with_vat}
                      onChange={(v) => handleInputChange('kp_amount_with_vat', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>GPL проекта с НДС</FieldLabel>
                    <MoneyInput
                      value={formData.gpl_project_with_vat}
                      onChange={(v) => handleInputChange('gpl_project_with_vat', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Вход проекта с НДС</FieldLabel>
                    <MoneyInput
                      value={formData.entry_cost_with_vat}
                      onChange={(v) => handleInputChange('entry_cost_with_vat', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ═══════════ БЛОК 3: ОБЯЗАТЕЛЬСТВА ═══════════ */}
            <Card>
              <CardContent className="pt-5 space-y-4">
                <BlockHeader title="ОБЯЗАТЕЛЬСТВА" color={BLOCK_COLORS.obligations} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <FieldLabel>Дедлайн проекта</FieldLabel>
                    <DatePickerField
                      value={formData.expected_end_date}
                      onChange={(v) => handleInputChange('expected_end_date', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Сумма договора с НДС</FieldLabel>
                    <MoneyInput
                      value={formData.contract_amount_with_vat}
                      onChange={(v) => handleInputChange('contract_amount_with_vat', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Дата подписания договора</FieldLabel>
                    <DatePickerField
                      value={formData.contract_sign_date}
                      onChange={(v) => handleInputChange('contract_sign_date', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Дата закрытия договора</FieldLabel>
                    <DatePickerField
                      value={formData.contract_close_date}
                      onChange={(v) => handleInputChange('contract_close_date', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Дата плановой оплаты Вендору</FieldLabel>
                    <DatePickerField
                      value={formData.vendor_payment_plan_date}
                      onChange={(v) => handleInputChange('vendor_payment_plan_date', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Сумма оплаты вендору (Факт)</FieldLabel>
                    <MoneyInput
                      value={formData.vendor_payment_fact_amount}
                      onChange={(v) => handleInputChange('vendor_payment_fact_amount', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Дата оплаты Заказчиком (Факт)</FieldLabel>
                    <DatePickerField
                      value={formData.client_payment_fact_date}
                      onChange={(v) => handleInputChange('client_payment_fact_date', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Ожидаемая доставка</FieldLabel>
                    <DatePickerField
                      value={formData.delivery_eta}
                      onChange={(v) => handleInputChange('delivery_eta', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Начало монтажа</FieldLabel>
                    <DatePickerField
                      value={formData.installation_start}
                      onChange={(v) => handleInputChange('installation_start', v)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
                {hasDeliveryRisk() && (
                  <div className="flex items-center gap-2 p-3 bg-orange-50 text-orange-800 rounded-md" data-testid="delivery-risk-warning">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Риск простоя:</span> Доставка позже начала монтажа!
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ═══════════ БЛОК 4: ЗАТРАТЫ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <Card>
                <CardContent className="pt-5 space-y-4">
                  <BlockHeader title="ЗАТРАТЫ" color={BLOCK_COLORS.expenses} />
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <FieldLabel>Затраты на оборудование, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_equipment}
                        onChange={(v) => handleInputChange('expenses_equipment', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Затраты на логистику, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_logistics}
                        onChange={(v) => handleInputChange('expenses_logistics', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Затраты на прочие расходы, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_other}
                        onChange={(v) => handleInputChange('expenses_other', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Затраты на монтаж/услуги, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_installation}
                        onChange={(v) => handleInputChange('expenses_installation', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Переработки, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_overtime}
                        onChange={(v) => handleInputChange('expenses_overtime', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Страхование</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_insurance}
                        onChange={(v) => handleInputChange('expenses_insurance', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Кредит</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_credit}
                        onChange={(v) => handleInputChange('expenses_credit', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>% за партнерство, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_partnership}
                        onChange={(v) => handleInputChange('expenses_partnership', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Пеня, тг</FieldLabel>
                      <MoneyInput
                        value={formData.expenses_penalty}
                        onChange={(v) => handleInputChange('expenses_penalty', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ═══════════ БЛОК 5: СУБПОДРЯД И НАЛОГИ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <Card>
                <CardContent className="pt-5 space-y-4">
                  <BlockHeader title="СУБПОДРЯД И НАЛОГИ" color={BLOCK_COLORS.subcontract} />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <FieldLabel>% субподряда</FieldLabel>
                      <PercentInput
                        value={formData.subcontract_percent}
                        onChange={(v) => handleInputChange('subcontract_percent', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Сумма (субподряда)</FieldLabel>
                      <MoneyInput
                        value={formData.subcontract_amount}
                        onChange={(v) => handleInputChange('subcontract_amount', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Налоги</FieldLabel>
                      <MoneyInput
                        value={formData.taxes_amount}
                        onChange={(v) => handleInputChange('taxes_amount', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ═══════════ БЛОК 6: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ (только для Finance Admin / Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <Card>
                <CardContent className="pt-5 space-y-4">
                  <BlockHeader title="ИТОГОВАЯ КАЛЬКУЛЯЦИЯ" color={BLOCK_COLORS.total} />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <FieldLabel>Маржа, тг</FieldLabel>
                      <MoneyInput
                        value={formData.result_margin}
                        onChange={(v) => handleInputChange('result_margin', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Маржа, %</FieldLabel>
                      <PercentInput
                        value={formData.result_margin_percent}
                        onChange={(v) => handleInputChange('result_margin_percent', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Налоги, тг</FieldLabel>
                      <MoneyInput
                        value={formData.result_taxes}
                        onChange={(v) => handleInputChange('result_taxes', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Валовая прибыль, тг</FieldLabel>
                      <MoneyInput
                        value={formData.result_gross_profit}
                        onChange={(v) => handleInputChange('result_gross_profit', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ═══════════ БЛОК 7: ПЛАН/ФАКТ (только для Finance Admin) ═══════════ */}
            {canViewFinanceBlocks && (
              <Card>
                <CardContent className="pt-5 space-y-4">
                  <BlockHeader title="ПЛАН / ФАКТ" color={BLOCK_COLORS.total} />
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <FieldLabel>План (бюджет), тг</FieldLabel>
                      <MoneyInput
                        value={formData.budget_plan}
                        onChange={(v) => handleInputChange('budget_plan', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Факт (получено), тг</FieldLabel>
                      <MoneyInput
                        value={formData.budget_fact}
                        onChange={(v) => handleInputChange('budget_fact', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <FieldLabel>Затраты (факт), тг</FieldLabel>
                      <MoneyInput
                        value={formData.costs_fact}
                        onChange={(v) => handleInputChange('costs_fact', v)}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ═══════════ БЛОК 8: РИСКИ ═══════════ */}
            <Card>
              <CardContent className="pt-5 space-y-4">
                <BlockHeader title="РИСКИ" color={BLOCK_COLORS.risk} />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <FieldLabel>Категория риска</FieldLabel>
                    <Select
                      value={formData.risk_category}
                      onValueChange={(v) => handleInputChange('risk_category', v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите" />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_CATEGORY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Вероятность (1-5)</FieldLabel>
                    <Select
                      value={String(formData.risk_probability)}
                      onValueChange={(v) => handleInputChange('risk_probability', v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="1-5" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <FieldLabel>Влияние (1-5)</FieldLabel>
                    <Select
                      value={String(formData.risk_impact)}
                      onValueChange={(v) => handleInputChange('risk_impact', v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="1-5" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {hasCriticalRisk() && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-md" data-testid="critical-risk-warning">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Критическая угроза!</span> Индекс риска &gt; 15
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comments Card: Bitrix timeline + manual status comment */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Комментарии
                    </CardTitle>
                    <CardDescription>
                      {project.bitrix_task_id
                        ? 'Комментарии из задачи Bitrix + ручной комментарий'
                        : 'Ручной комментарий к проекту'}
                    </CardDescription>
                  </div>
                  {project.bitrix_task_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => loadBitrixComments(project.bitrix_task_id)}
                      disabled={commentsLoading}
                    >
                      <RefreshCw className={cn("w-4 h-4", commentsLoading && "animate-spin")} />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Bitrix comments timeline */}
                {project.bitrix_task_id && (
                  <div className="space-y-3">
                    {commentsLoading ? (
                      <div className="flex items-center justify-center py-4 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        <span className="text-sm">Загрузка комментариев...</span>
                      </div>
                    ) : bitrixComments.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        {bitrixComments.map((comment) => (
                          <div key={comment.id} className="border rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {comment.author_name} {comment.author_last_name}
                              </span>
                              <span className="text-xs text-gray-400">
                                {comment.date ? format(new Date(comment.date), 'dd.MM.yyyy HH:mm', { locale: ru }) : ''}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {comment.message.replace(/\[.*?\]/g, '').trim()}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-2">
                        Нет комментариев в задаче Bitrix
                      </p>
                    )}
                    <hr className="border-gray-200" />
                  </div>
                )}

                {/* Manual status comment — всегда редактируемый для всех ролей */}
                <div className="space-y-1">
                  <FieldLabel>Итоговый комментарий (для отчётов)</FieldLabel>
                  <Textarea
                    value={formData.status_comment}
                    onChange={(e) => handleInputChange('status_comment', e.target.value)}
                    onBlur={(e) => handleStatusCommentSave(e.target.value)}
                    rows={3}
                    placeholder="Итоговый комментарий менеджера по проекту..."
                    data-testid="status-comment-textarea"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Project Stages */}
            <Card>
              <CardHeader>
                <CardTitle>Этапы проекта</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {project.stages.map((stage, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{stage.name}</div>
                        <Badge variant={stage.status === 'completed' ? 'success' : stage.status === 'in_progress' ? 'default' : 'secondary'}>
                          {stage.status === 'completed' ? 'Завершен' : stage.status === 'in_progress' ? 'В работе' : 'Ожидание'}
                        </Badge>
                      </div>
                      <Progress value={stage.progress_percent} className="mb-2" />
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Прогресс: {stage.progress_percent}%</span>
                        {stage.deadline && <span>Дедлайн: {formatDateShort(stage.deadline)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* История статусов */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  История статусов
                </CardTitle>
                <CardDescription>
                  {project.project_status ? (
                    <span>Текущий статус: <strong>{project.project_status}</strong></span>
                  ) : (
                    <span className="text-red-600 font-semibold">Статус не установлен</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {project.status_history && project.status_history.length > 0 ? (
                  <div className="space-y-3">
                    {project.status_history.slice().reverse().map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-gray-900 truncate">
                              {entry.status}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {entry.changed_at
                                ? format(new Date(entry.changed_at), 'dd.MM.yyyy HH:mm', { locale: ru })
                                : ''}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {entry.changed_by || 'Система'}
                            {entry.previous_status && (
                              <span className="ml-2 text-gray-400">
                                ← {entry.previous_status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">
                    История изменений пуста
                  </p>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Sidebar - Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Финансовая сводка</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Выручка */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Бюджет заказчика:</span>
                    <span className="font-semibold text-blue-600">
                      {project.client_budget_with_vat ? formatCurrency(project.client_budget_with_vat) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">КП с НДС:</span>
                    <span className="font-semibold">
                      {project.kp_amount_with_vat ? formatCurrency(project.kp_amount_with_vat) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Договор с НДС:</span>
                    <span className="font-semibold">
                      {project.contract_amount_with_vat ? formatCurrency(project.contract_amount_with_vat) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GPL с НДС:</span>
                    <span className="font-semibold">
                      {project.gpl_project_with_vat ? formatCurrency(project.gpl_project_with_vat) : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Вход с НДС:</span>
                    <span className="font-semibold">
                      {project.entry_cost_with_vat ? formatCurrency(project.entry_cost_with_vat) : '—'}
                    </span>
                  </div>
                </div>

                {/* Затраты - скрыто для Senior Manager */}
                {canViewFinanceBlocks && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3">Затраты</h4>
                    {(() => {
                      const totalExpenses = [
                        project.expenses_equipment, project.expenses_logistics,
                        project.expenses_other, project.expenses_installation,
                        project.expenses_overtime, project.expenses_insurance,
                        project.expenses_credit, project.expenses_partnership,
                        project.expenses_penalty
                      ].reduce((sum, v) => sum + (v || 0), 0);
                      const hasExpenses = totalExpenses > 0;
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Итого затрат:</span>
                            <span className={cn("font-semibold", hasExpenses ? "text-red-600" : "text-gray-400")}>
                              {hasExpenses ? formatCurrency(totalExpenses) : '—'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Субподряд:</span>
                            <span className="font-semibold">
                              {project.subcontract_amount
                                ? `${formatCurrency(project.subcontract_amount)}${project.subcontract_percent ? ` (${project.subcontract_percent}%)` : ''}`
                                : '—'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Налоги:</span>
                            <span className="font-semibold">
                              {project.taxes_amount ? formatCurrency(project.taxes_amount) : '—'}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Маржинальность - скрыто для Senior Manager */}
                {canViewFinanceBlocks && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3">Маржинальность</h4>
                    {project.result_margin_percent != null ? (
                      <div className={cn(
                        "p-3 rounded-lg border-2",
                        project.result_margin_percent >= 15
                          ? "bg-green-50 border-green-200"
                          : project.result_margin_percent >= 5
                            ? "bg-yellow-50 border-yellow-200"
                            : "bg-red-50 border-red-200"
                      )}>
                        <div className="text-sm text-gray-600">Маржа</div>
                        <div className={cn(
                          "text-2xl font-bold",
                          project.result_margin_percent >= 15
                            ? "text-green-600"
                            : project.result_margin_percent >= 5
                              ? "text-yellow-600"
                              : "text-red-600"
                        )}>
                          {formatPercent(project.result_margin_percent)}
                        </div>
                        {project.result_margin != null && (
                          <div className="text-xs text-gray-500">
                            {formatCurrency(project.result_margin)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-100 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600">Маржа</div>
                        <div className="text-xl font-bold text-gray-400">—</div>
                        <div className="text-xs text-gray-400">Нет данных</div>
                      </div>
                    )}
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Валовая прибыль:</span>
                        <span className={cn(
                          "font-semibold",
                          project.result_gross_profit > 0 ? "text-green-600" : project.result_gross_profit < 0 ? "text-red-600" : "text-gray-400"
                        )}>
                          {project.result_gross_profit != null ? formatCurrency(project.result_gross_profit) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Оплаты (факт) */}
                {project.vendor_payment_fact_amount != null && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-3">Оплаты</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Вендору (факт):</span>
                        <span className="font-semibold text-red-600">
                          {formatCurrency(project.vendor_payment_fact_amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Даты</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Начало:</span>
                  <span className="font-semibold">{formatDateShort(project.start_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Дедлайн:</span>
                  <span className="font-semibold">{formatDateShort(project.expected_end_date)}</span>
                </div>
                {formData.ts_deadline && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Срок ТС:</span>
                    <span className="font-semibold">{format(formData.ts_deadline, 'dd.MM.yyyy')}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bitrix24 Integration Block */}
            {(project.bitrix_task_id || project.is_from_bitrix) && (
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                    Bitrix24
                  </CardTitle>
                  <CardDescription className="text-xs text-blue-600">
                    Данные синхронизируются автоматически
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {/* Бюджет из Bitrix (read-only) */}
                  {(project.budget_wo_vat != null || project.budget_no_vat != null) && (
                    <div className="p-3 bg-white rounded-lg border border-blue-200" data-testid="bitrix-budget-block">
                      <div className="text-xs text-gray-500 mb-1">Бюджет (без НДС)</div>
                      <div className="text-lg font-bold text-blue-700">
                        {formatCurrency(project.budget_wo_vat || project.budget_no_vat)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Источник: Bitrix</div>
                    </div>
                  )}
                  
                  {project.bitrix_task_url && (
                    <a
                      href={project.bitrix_task_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                      data-testid="bitrix-task-link"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Основная задача
                    </a>
                  )}
                  
                  {project.bitrix_ts_task_url && (
                    <a
                      href={project.bitrix_ts_task_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
                      data-testid="bitrix-ts-link"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Подзадача ТС
                    </a>
                  )}
                  
                  {project.bitrix_last_sync_at && (
                    <div className="pt-2 border-t border-blue-200 text-xs text-gray-500">
                      Последняя синхронизация: {formatDateShort(project.bitrix_last_sync_at)}
                    </div>
                  )}
                  
                  {project.pcc_status === 'Closed' && (
                    <Badge variant="secondary" className="bg-yellow-200 text-yellow-800">
                      Закрыт
                    </Badge>
                  )}
                  {project.bitrix_task_missing && (
                    <Badge variant="outline" className="border-orange-400 text-orange-700">
                      ⚠️ Задача удалена в Bitrix
                    </Badge>
                  )}
                </CardContent>
              </Card>
            )}

            {!canEdit && (
              <div className="p-4 bg-gray-100 rounded-lg text-sm text-gray-600">
                <p className="font-semibold">Режим просмотра</p>
                <p>Редактирование доступно только для Admin и Moderator</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Close Confirmation Dialog (Stage 11.6) */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Закрыть проект?</AlertDialogTitle>
            <AlertDialogDescription>
              Проект будет помечен как закрытый, скрыт из основных списков и исключён из финансовых агрегатов. Это действие можно отменить позже.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClose}
              disabled={closing}
              className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-600"
            >
              {closing ? 'Закрытие...' : 'Закрыть проект'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog (Stage 11.6) */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isBitrixLinked ? 'Удалить проект (связан с Bitrix)?' : 'Удалить проект?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isBitrixLinked ? (
                <>
                  Проект связан с задачей Bitrix. Удаление в PCC не удалит задачу в Bitrix.
                  <br /><br />
                  Проект будет исключён из финансовых отчётов. Восстановление возможно только администратором.
                </>
              ) : (
                'Проект будет исключён из финансовых отчётов. Восстановление возможно только администратором.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage 11.4: Archive Confirmation Dialog */}
      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Архивировать проект?</AlertDialogTitle>
            <AlertDialogDescription>
              Завершённый проект будет архивирован. Он останется в финансовых отчётах.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiving}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-600"
            >
              {archiving ? 'Архивирование...' : 'Архивировать'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage 11.4: Unarchive Confirmation Dialog */}
      <AlertDialog open={showUnarchiveConfirm} onOpenChange={setShowUnarchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Разархивировать проект?</AlertDialogTitle>
            <AlertDialogDescription>
              Проект будет возвращён в активные.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unarchiving}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnarchive}
              disabled={unarchiving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-600"
            >
              {unarchiving ? 'Разархивирование...' : 'Разархивировать'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage 11.4: Reopen Confirmation Dialog */}
      <AlertDialog open={showReopenConfirm} onOpenChange={setShowReopenConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Переоткрыть проект?</AlertDialogTitle>
            <AlertDialogDescription>
              Проект снова появится в активных отчётах.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopening}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReopen}
              disabled={reopening}
              className="bg-orange-600 hover:bg-orange-700 focus:ring-orange-600"
            >
              {reopening ? 'Переоткрытие...' : 'Переоткрыть'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage 11.4: Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Восстановить проект?</AlertDialogTitle>
            <AlertDialogDescription>
              Удалённый проект будет восстановлен.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoring}
              className="bg-teal-600 hover:bg-teal-700 focus:ring-teal-600"
            >
              {restoring ? 'Восстановление...' : 'Восстановить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectDetailPage;
